import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import { useInView } from '../home/hooks/useInView';

const Cugnaux = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Réparation de fenêtres à Cugnaux – Intervention rapide 24h/24 | Vitrerie 31';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert réparation fenêtres à Cugnaux (31270). Intervention sous 2h pour fenêtres PVC, bois, alu. Devis gratuit. ☎ 05 54 54 25 45');
    }
  }, []);

  const services = [
    { icon: 'ri-tools-line', title: 'Réparation Fenêtres PVC', desc: 'Joints, mécanismes, vitrage' },
    { icon: 'ri-hammer-line', title: 'Fenêtres Bois', desc: 'Rénovation et entretien' },
    { icon: 'ri-window-line', title: 'Fenêtres Aluminium', desc: 'Réparation et remplacement' },
    { icon: 'ri-shield-check-line', title: 'Vitrerie d\'urgence', desc: 'Intervention 24h/24' }
  ];

  const testimonials = [
    {
      name: 'Sandrine Delmas',
      location: 'Quartier Villeneuve',
      text: 'Fenêtre qui ne fermait plus. Intervention rapide et efficace. Le technicien a été très professionnel et sympathique.',
      rating: 5
    },
    {
      name: 'Christophe Arnaud',
      location: 'Centre-ville Cugnaux',
      text: 'Remplacement de vitrage cassé. Service impeccable, prix honnête. Je recommande sans hésiter !',
      rating: 5
    },
    {
      name: 'Monique Ferrer',
      location: 'Quartier Récébédou',
      text: 'Problème d\'étanchéité résolu rapidement. Équipe compétente et à l\'écoute. Très satisfaite !',
      rating: 5
    }
  ];

  const problemesTypiques = [
    { icon: 'ri-water-flash-line', title: 'Infiltrations d\'eau', desc: 'Joints dégradés par l\'humidité' },
    { icon: 'ri-wind-line', title: 'Courants d\'air', desc: 'Isolation thermique défaillante' },
    { icon: 'ri-lock-unlock-line', title: 'Mécanismes grippés', desc: 'Usure et entretien' },
    { icon: 'ri-contrast-drop-line', title: 'Condensation', desc: 'Double vitrage à remplacer' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-map-pin-line"></i>
              <span>Cugnaux - Haute-Garonne (31270)</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Réparation de fenêtres à <strong className="text-teal-600">Cugnaux</strong>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Intervention rapide <strong>sous 2h</strong> pour tous vos problèmes de fenêtres
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="tel:0554542545"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
              >
                <i className="ri-phone-line text-xl"></i>
                05 54 54 25 45
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-teal-600 active:scale-95"
              >
                <i className="ri-message-line text-xl"></i>
                Devis gratuit
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Intervention rapide</h3>
                <p className="text-gray-600 text-sm">Sous 2h à Cugnaux</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-2-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">À 8 km de Toulouse</h3>
                <p className="text-gray-600 text-sm">Tous quartiers desservis</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Urgences 24h/24</h3>
                <p className="text-gray-600 text-sm">7 jours sur 7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services à <strong className="text-teal-600">Cugnaux</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experts en réparation de fenêtres pour tous les quartiers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problèmes Typiques */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Problèmes fréquents à <strong className="text-teal-600">Cugnaux</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Nous connaissons les spécificités de votre commune
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemesTypiques.map((probleme, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${probleme.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{probleme.title}</h3>
                <p className="text-gray-600 text-sm">{probleme.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-500 to-orange-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Pourquoi ces problèmes à Cugnaux ?</h3>
            <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
              Située à <strong>8 km au sud-ouest de Toulouse</strong>, Cugnaux est une commune résidentielle dynamique. Les quartiers <strong>Villeneuve, Récébédou et le Centre-ville</strong> sont particulièrement touchés par les problèmes d'infiltration et de condensation liés au climat toulousain. Nos équipes interviennent rapidement dans tous les secteurs de la commune.
            </p>
          </div>
        </div>
      </section>

      {/* Témoignages Clients */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos clients à <strong className="text-teal-600">Cugnaux</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils nous ont fait confiance pour leurs fenêtres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOUVEAU BLOC 1 - Triple Vitrage Haute Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/43ddbd1a5099444daa317f28ce9a8b07.jpg"
              alt="Triple vitrage haute performance Cugnaux"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-stack-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">Triple Vitrage</p>
                  <p className="text-sm text-gray-600">Performance max</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-teal-600">Triple Vitrage Haute Performance</strong> à Cugnaux
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Le <strong>triple vitrage</strong> représente le summum de l'isolation thermique et phonique pour votre habitation à Cugnaux. Notre expertise en <strong>installation de triple vitrage à Cugnaux</strong> vous permet d'atteindre des performances énergétiques exceptionnelles avec un coefficient Uw jusqu'à 0,5 W/m².K, idéal pour les maisons passives et BBC.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nous proposons l'<strong>installation de fenêtres triple vitrage</strong> avec <strong>trois couches de verre</strong> séparées par deux lames d'argon ou de krypton, <strong>vitrage à isolation renforcée (VIR)</strong> avec couche faiblement émissive, et <strong>intercalaires warm edge</strong> pour éliminer les ponts thermiques. Le triple vitrage offre également une excellente isolation phonique à Cugnaux.
          </p>

          <div className="bg-gradient-to-r from-teal-50 to-green-50 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-leaf-line text-green-600 mr-2 text-xl"></i>
              Performances Exceptionnelles
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-teal-600 mb-1">0,5</p>
                <p className="text-sm text-gray-600">Coefficient Uw W/m².K</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-orange-600 mb-1">40%</p>
                <p className="text-sm text-gray-600">Économies énergie</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-start">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-temp-cold-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Isolation Thermique Maximale</h4>
                <p className="text-gray-600 text-sm">3 fois plus isolant que le double vitrage standard, idéal maisons passives</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-volume-mute-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Isolation Phonique Renforcée</h4>
                <p className="text-gray-600 text-sm">Réduction du bruit jusqu'à 45 dB grâce aux 3 couches de verre</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <i className="ri-money-euro-circle-line text-2xl text-green-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Aides Financières Majorées</h4>
                <p className="text-gray-600 text-sm">MaPrimeRénov' bonifiée pour triple vitrage, éco-PTZ, TVA 5,5%</p>
              </div>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Devis Triple Vitrage Gratuit
          </a>
        </motion.div>
      </div>

      {/* NOUVEAU BLOC 2 - Fenêtres Bois Contemporaines */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="order-2 lg:order-1"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            <strong className="text-orange-600">Fenêtres Bois Contemporaines</strong> à Cugnaux
          </h3>
          <p className="text-gray-600 mb-4 leading-relaxed">
            Les <strong>fenêtres en bois</strong> allient authenticité, élégance naturelle et excellentes performances thermiques. Notre gamme de <strong>menuiseries bois contemporaines à Cugnaux</strong> propose des designs modernes avec des essences nobles (chêne, méranti, pin) et des finitions haut de gamme pour sublimer votre habitation.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Nous proposons l'<strong>installation de fenêtres bois à Cugnaux</strong> avec <strong>double ou triple vitrage</strong>, <strong>traitement haute protection</strong> contre les intempéries et les insectes, <strong>large choix de teintes et finitions</strong> (lasure, peinture, vernis), et <strong>isolation thermique renforcée</strong>. Le bois est un matériau naturellement isolant et écologique, idéal pour une maison saine à Cugnaux.
          </p>

          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center">
              <i className="ri-plant-line text-orange-600 mr-2 text-xl"></i>
              Avantages Fenêtres Bois
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3 text-center">
                <i className="ri-leaf-line text-2xl text-green-600 mb-1"></i>
                <p className="text-xs text-gray-600 font-semibold">Matériau écologique</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <i className="ri-temp-cold-line text-2xl text-teal-600 mb-1"></i>
                <p className="text-xs text-gray-600 font-semibold">Isolation naturelle</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <i className="ri-palette-line text-2xl text-orange-600 mb-1"></i>
                <p className="text-xs text-gray-600 font-semibold">Esthétique noble</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <i className="ri-recycle-line text-2xl text-green-600 mb-1"></i>
                <p className="text-xs text-gray-600 font-semibold">100% recyclable</p>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Essences nobles : chêne, méranti, pin douglas</span>
            </div>
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Traitement autoclave classe 4 contre humidité</span>
            </div>
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Finitions lasure, peinture ou vernis haute qualité</span>
            </div>
            <div className="flex items-center text-gray-700">
              <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
              <span>Garantie 10 ans sur bois et finitions</span>
            </div>
          </div>

          <a
            href="#contact"
            className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Devis Fenêtres Bois Gratuit
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="relative order-1 lg:order-2"
        >
          <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=beautiful%20contemporary%20wooden%20window%20frame%20with%20natural%20wood%20grain%20texture%20showing%20high%20quality%20craftsmanship%20modern%20design%20elegant%20finish%20installed%20in%20stylish%20home%20demonstrating%20warmth%20authenticity%20sustainable%20material%20eco%20friendly%20choice%20simple%20clean%20background&width=800&height=600&seq=cugnaux-wood-window-v1&orientation=landscape"
              alt="Fenêtres bois contemporaines Cugnaux"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-amber-900/60 via-transparent to-transparent"></div>
            
            <div className="absolute bottom-6 right-6 bg-white rounded-xl shadow-xl p-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="ri-plant-line text-amber-600 text-2xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">Bois Noble</p>
                  <p className="text-sm text-gray-600">Élégance naturelle</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quartiers desservis */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous les quartiers de <strong className="text-teal-600">Cugnaux</strong>
            </h2>
            <p className="text-lg text-gray-600">Intervention rapide dans toute la commune</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Centre-ville', 'Villeneuve', 'Récébédou', 'Bordelongue', 'Ferradou', 'Prat Long', 'Ramelet', 'Mourgues'].map((quartier, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all text-center"
              >
                <i className="ri-map-pin-2-fill text-2xl text-teal-600 mb-2"></i>
                <p className="font-semibold text-gray-900">{quartier}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une réparation de fenêtre à Cugnaux ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Intervention rapide sous 2h - Devis gratuit - Urgences 24h/24
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0554542545"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez maintenant
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-message-line text-xl"></i>
              Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Liens vers autres villes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Nous intervenons aussi dans les villes voisines
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Toulouse</a>
            <span className="text-gray-400">•</span>
            <a href="/colomiers" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Colomiers</a>
            <span className="text-gray-400">•</span>
            <a href="/blagnac" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Blagnac</a>
            <span className="text-gray-400">•</span>
            <a href="/tournefeuille" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Tournefeuille</a>
            <span className="text-gray-400">•</span>
            <a href="/muret" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Muret</a>
            <span className="text-gray-400">•</span>
            <a href="/balma" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Balma</a>
            <span className="text-gray-400">•</span>
            <a href="/ramonville" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Ramonville</a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default Cugnaux;
